import {useState, useEffect, useCallback, useMemo} from 'react';

import {ObservableMap} from 'mobx';

import {useStores} from '../../../Hooks';
import {procedureTypeEnum} from '../../../serverClientShared/sharedEnums';

const buildConnectionsMap = (procedures) => {
    const map = new Map();
    procedures.forEach((p) => {
        if (p.ProcedureType !== procedureTypeEnum.REGULAR || !p.ThreeDModelsConnected) return;
        p.ThreeDModelsConnected.forEach((modelId) => {
            const set = map.get(modelId) || new Set();
            set.add(p.ProcedureIdentifier);
            map.set(modelId, set);
        });
    });
    return map;
};

const cloneMap = (map) => {
    const next = new Map();
    map.forEach((set, key) => next.set(key, new Set(set)));
    return next;
};

const setsEqual = (a, b) => a.size === b.size && [...a].every((id) => b.has(id));

const mapsEqual = (a, b) => {
    const keys = new Set([...a.keys(), ...b.keys()]);
    for (const key of keys) {
        if (!setsEqual(a.get(key) || new Set(), b.get(key) || new Set())) return false;
    }
    return true;
};

const useConnectedFlowsState = (projectId, isMirrored) => {
    const {projectStore, procedureStore, dataStore, modalStore, generalStore} = useStores();
    const projectProceduresMap = dataStore.projectsProceduresMap.get(projectId);
    const projectFoldersMap = dataStore.projectsFoldersMap.get(projectId);

    const [originalMap, setOriginalMap] = useState(new Map());
    const [editedMap, setEditedMap] = useState(new Map());
    const [regularProcedures, setRegularProcedures] = useState([]);
    const [folders, setFolders] = useState([]);

    const refresh = useCallback(() => {
        const procedures = Array.from((projectProceduresMap || new ObservableMap()).values());
        const built = buildConnectionsMap(procedures);
        setOriginalMap(built);
        setEditedMap(cloneMap(built));
        const regular = procedures.filter((p) => p.ProcedureType === procedureTypeEnum.REGULAR);
        setRegularProcedures(isMirrored ? regular.filter((p) => !p.importedFromWorkspace) : regular);
    }, [projectProceduresMap, isMirrored]);

    useEffect(() => { refresh(); }, [refresh]);
    useEffect(() => {
        setFolders(Array.from((projectFoldersMap || new ObservableMap()).values()).filter((f) => f.FolderIdentifier !== 'root'));
    }, [projectFoldersMap]);

    const setModelConnections = useCallback((modelId, selectedSet) => {
        setEditedMap((prev) => {
            const next = cloneMap(prev);
            next.set(modelId, new Set(selectedSet));
            return next;
        });
    }, []);

    const isDirty = useMemo(() => !mapsEqual(originalMap, editedMap), [originalMap, editedMap]);

    const discard = useCallback(() => setEditedMap(cloneMap(originalMap)), [originalMap]);

    const save = useCallback(async () => {
        const changed = [];
        editedMap.forEach((set, modelId) => {
            if (!setsEqual(set, originalMap.get(modelId) || new Set())) changed.push([modelId, set]);
        });
        if (!changed.length) return;
        try {
            for (const [modelId, set] of changed) {
                await procedureStore.updateMultipleConnections(modelId, set);
            }
            await projectStore.setProceduresFromDB(projectId);
        } catch (error) {
            console.error('Failed to save connected flows:', error);
            modalStore.showErrorMessage(generalStore.translate('couldNotUpdateConnections') || 'Failed to update connections.');
        }
    }, [editedMap, originalMap, procedureStore, projectStore, projectId, modalStore, generalStore]);

    return {connectionsMap: editedMap, regularProcedures, folders, setModelConnections, isDirty, save, discard};
};

export default useConnectedFlowsState;
